import React from 'react';

export interface StylishNewCheckboxProps {
  className?: string;
  disabled?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  required?: boolean;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  key?: React.Key;
  label?: string;
  bordered?: boolean;
  indeterminate?: boolean;
}

const StylishNewCheckbox: React.ForwardRefExoticComponent<
  StylishNewCheckboxProps &
    React.RefAttributes<HTMLInputElement> &
    React.InputHTMLAttributes<HTMLInputElement>
> = React.forwardRef(
  (
    {
      className,
      disabled,
      checked,
      defaultChecked,
      required,
      onClick,
      label,
      bordered = true,
      indeterminate,
      ...props
    },
    ref
  ) => {
    // Forward ref to parent component and maintain local ref for setting indeterminate state
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const setRefs = (el: HTMLInputElement | null) => {
      inputRef.current = el;
      if (typeof ref === 'function') {
        ref(el);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
      }
    };
    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = !!indeterminate;
      }
    }, [indeterminate]);

    const inputId = `${props.id ?? props.name ?? label}-checkbox`;
    return (
      <>
        <div
          className={
            'form-checkbox align-items-center ' + (className ? className : '')
          }
        >
          <>
            <input
              type="checkbox"
              name={props.name || label}
              ref={setRefs}
              {...props}
              id={inputId}
              disabled={disabled}
              defaultChecked={defaultChecked}
              checked={checked}
              onClick={onClick}
              readOnly
            />
            <span
              className="icon"
              style={{
                border: bordered ? '1px solid var(--neutral-100)' : undefined,
              }}
            ></span>
          </>
          <label className="form-label text-white" htmlFor={inputId}>
            {label}
            {required ? (
              <span aria-label="Required field" className="required">
                *
              </span>
            ) : null}
          </label>
        </div>
      </>
    );
  }
);

StylishNewCheckbox.displayName = 'StylishNewCheckbox';

export { StylishNewCheckbox };
